import { CategoryKey, type TaxMetadata } from '#dictionary'
import ProfileService from '#profile/ProfileService'

import type { ITaxer } from '../Taxer'
import createTimelineRecord from '../timeline/createTimelineRecord'
import type { TimelineRecord } from '../timeline/TimelineRecord'
import type {
  TaxCalculationResult,
  TotalTaxesCalculationResult,
} from '../types'

type GenerationResult = {
  timelineRecords: TimelineRecord[]
  taxCalculationResult: TotalTaxesCalculationResult | null
}

class TaxesGenerator {
  constructor(
    private readonly taxer: ITaxer,
    private readonly profile: ProfileService
  ) {}

  generate(year: number, timelineRecords: TimelineRecord[]): GenerationResult {
    if (year < this.profile.projectionStartYear) {
      return { timelineRecords: [], taxCalculationResult: null }
    }

    const taxTimelineRecords: TimelineRecord[] = []

    const taxCalculationResult = this.taxer.calculateTotalTaxes(
      year,
      timelineRecords
    )

    const { federalTaxCreditAmount, taxes } = taxCalculationResult

    taxes.forEach(calculatedTax => {
      const tax = calculatedTax.taxMetadata

      const taxTimelineRecord = createTimelineRecord({
        year,
        value: calculatedTax.totalTax,
        description: this.generateDescription(tax, calculatedTax, year),
        categoryKey: tax.fillingCategoryKey,
        intent: 'Tax',
      })

      taxTimelineRecords.push(taxTimelineRecord)
    })

    if (federalTaxCreditAmount !== 0) {
      const federalTaxCreditRecord = createTimelineRecord({
        year,
        value: -federalTaxCreditAmount,
        description: 'Federal Tax Credit',
        categoryKey: CategoryKey.catTaxesCreditFederal,
        intent: 'Tax',
      })

      taxTimelineRecords.push(federalTaxCreditRecord)
    }

    return {
      timelineRecords: taxTimelineRecords,
      taxCalculationResult,
    }
  }

  private generateDescription = (
    tax: TaxMetadata,
    taxCalculationResult: TaxCalculationResult,
    year: number
  ): string => {
    const title =
      (taxCalculationResult.description || tax.name) + ' (' + year + ')'

    const effectiveRatePercent = (
      taxCalculationResult.effectiveRate * 100
    ).toFixed(2)

    // Calculate bracket ranges and format as table
    let bracketTable = ''

    if (
      taxCalculationResult.bracketBreakdown.length === 0 ||
      taxCalculationResult.taxableIncome === 0
    ) {
      bracketTable = `**No taxable income - no tax brackets apply**`
    } else {
      let runningTotal = 0
      const bracketRows = taxCalculationResult.bracketBreakdown.map(
        (bracket, _index) => {
          const incomeInBracket = bracket.incomeInBracket
          const rate = bracket.taxRate * 100
          const tax = incomeInBracket * bracket.taxRate

          const startRange = runningTotal
          const endRange = bracket.to
          runningTotal = endRange

          // Replace the highest bracket value with "Infinity"
          const displayEndRange =
            endRange > 1000000000 ? 'Infinity' : `$${endRange.toFixed(0)}`

          return `| $${startRange.toFixed(0)} - ${displayEndRange} | ${rate.toFixed(1)}% | $${incomeInBracket.toFixed(0)} | $${tax.toFixed(0)} |`
        }
      )

      bracketTable = `| Income Range | Rate | Taxable income | Tax |
|--------------|------|---------------|-----|
${bracketRows.join('\n')}
| **Total:** | **Effective rate: ${effectiveRatePercent}%** | **$${taxCalculationResult.taxableIncome.toFixed(0)}** | **$${taxCalculationResult.totalTax.toFixed(0)}** |`
    }

    return `## ${title}

### Tax Deductions

**Standard Deduction Applied:** $${taxCalculationResult.standardDeduction.toFixed(0)}

### Bracket Breakdown

${bracketTable}`
  }
}

export default TaxesGenerator
